import { resolveTranslationLanguageCode } from "@/lib/translation-language";
import { useCallback, useEffect, useRef, useState } from "react";

interface TranslationSettingsOptions {
  isActiveSession: boolean;
  targetLanguageCode: string;
  defaultLanguageCode?: string;
  languageCode?: string;
  onLanguageChange?: (code: string) => void;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
}

export function useTranscriptTranslationSettings({
  isActiveSession,
  targetLanguageCode,
  defaultLanguageCode,
  languageCode,
  onLanguageChange,
  enabled,
  onEnabledChange,
}: TranslationSettingsOptions) {
  const [localLanguageCode, setLocalLanguageCode] = useState<string>();
  const [localEnabled, setLocalEnabled] = useState(false);
  const wasActive = useRef(false);
  const effectiveLanguageCode = resolveTranslationLanguageCode({
    targetLanguageCode,
    preferredLanguageCode: languageCode ?? localLanguageCode,
    guidedInstructionLanguageCode: defaultLanguageCode,
  });

  useEffect(() => {
    if (enabled === undefined && !isActiveSession) setLocalEnabled(false);
    if (languageCode === undefined && isActiveSession && !wasActive.current) {
      setLocalLanguageCode(undefined);
    }
    wasActive.current = isActiveSession;
  }, [enabled, isActiveSession, languageCode]);

  const setLanguageCode = useCallback(
    (code: string) => {
      if (languageCode === undefined) setLocalLanguageCode(code);
      onLanguageChange?.(code);
    },
    [languageCode, onLanguageChange]
  );
  const setEnabled = useCallback(
    (value: boolean) => {
      if (enabled === undefined) setLocalEnabled(value);
      onEnabledChange?.(value);
    },
    [enabled, onEnabledChange]
  );

  return {
    translationLanguageCode: effectiveLanguageCode,
    translationsEnabled: enabled ?? localEnabled,
    setTranslationLanguageCode: setLanguageCode,
    setTranslationsEnabled: setEnabled,
  };
}
